/* Bemora Noir — theme.js (vanilla; GSAP and Lenis are loaded on demand from assets/) */
(() => {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, wait = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  const routes = (window.theme && window.theme.routes) || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };
  const strings = (window.theme && window.theme.strings) || {};

  const toast = (msg) => {
    const el = $('.toast'); if (!el) return;
    el.textContent = msg; el.hidden = false;
    clearTimeout(el._t); el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };

  /* ---------- Page ready & transitions ---------- */
  const ready = () => requestAnimationFrame(() => document.body.classList.add('is-ready'));
  if (document.readyState === 'complete') ready(); else window.addEventListener('load', ready);
  setTimeout(ready, 1600);
  window.addEventListener('pageshow', (e) => { if (e.persisted) { document.body.classList.remove('is-leaving'); ready(); } });

  if ($('.page-veil') && !reduceMotion) {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('#') || a.target === '_blank' || a.hasAttribute('download') || a.dataset.noTransition !== undefined) return;
      let url; try { url = new URL(a.href); } catch (_) { return; }
      if (url.origin !== location.origin || (url.pathname === location.pathname && url.hash)) return;
      if (/\/(checkout|cart\/|account)/.test(url.pathname)) return;
      e.preventDefault();
      document.body.classList.add('is-leaving');
      setTimeout(() => { location.href = a.href; }, 320);
    });
  }

  /* ---------- Light image protection ---------- */
  // No right-click menu or dragging on artwork images (keep in sync with the CSS in base.css).
  // Text, keyboard use, links and screenshots are unaffected; the whole wall preview counts as imagery.
  const PROTECTED_AREAS = '.product__gallery, .card__media, .predictive__item, .mosaic__item, .collection-hero, .room-stage, .room-thumbs';
  const blockOnImage = (e) => {
    const el = e.target;
    if (el instanceof Element && el.closest(PROTECTED_AREAS) && el.closest('img, .room-stage')) e.preventDefault();
  };
  document.addEventListener('contextmenu', blockOnImage);
  document.addEventListener('dragstart', blockOnImage);

  /* ---------- Body lock ---------- */
  let lockCount = 0;
  const lock = () => { lockCount++; document.body.classList.add('is-locked'); if (lenis) lenis.stop(); };
  const unlock = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) { document.body.classList.remove('is-locked'); if (lenis) lenis.start(); } };

  /* ---------- Generic overlays (drawers / modals) ---------- */
  const openPanel = (id) => {
    const el = document.getElementById(id); if (!el || el.classList.contains('is-open')) return;
    el._opener = document.activeElement;
    el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false'); lock();
    const focusable = el.querySelector('[data-autofocus], input[type="search"], button, a[href]');
    setTimeout(() => focusable && focusable.focus({ preventScroll: true }), 120);
    el.dispatchEvent(new CustomEvent('panel:open'));
  };
  const closePanel = (el) => {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true'); unlock();
    if (el._opener) el._opener.focus({ preventScroll: true });
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      if (opener.dataset.open === 'cart-drawer' && window.theme && window.theme.cartType === 'page') return;
      e.preventDefault(); openPanel(opener.dataset.open); return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) { e.preventDefault(); closePanel(closer.closest('.is-open')); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { const open = $$('.drawer.is-open, .menu-drawer.is-open, .search-overlay.is-open, .modal.is-open').pop(); closePanel(open); }
  });

  /* ---------- Header ---------- */
  const headerWrap = $('.header-wrapper');
  if (headerWrap) {
    let lastY = window.scrollY; let ticking = false;
    const hideOnScroll = headerWrap.dataset.sticky === 'smart';
    const update = () => {
      const y = window.scrollY;
      headerWrap.classList.toggle('is-scrolled', y > 20);
      if (hideOnScroll) {
        const hide = y > lastY && y > 300 && !$('.nav__item:hover');
        headerWrap.classList.toggle('is-hidden', hide);
        document.body.classList.toggle('header-visible', !hide && y > 20);
      } else {
        document.body.classList.toggle('header-visible', y > 20);
      }
      lastY = y; ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
    const setH = () => document.documentElement.style.setProperty('--header-h', headerWrap.querySelector('.header') ? headerWrap.querySelector('.header').offsetHeight + 'px' : '76px');
    setH(); window.addEventListener('resize', debounce(setH, 150));
  }

  // Menu drawer accordions
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-submenu-toggle]'); if (!t) return;
    e.preventDefault();
    t.setAttribute('aria-expanded', t.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
  });

  /* ---------- Reveal ---------- */
  const revealIO = ('IntersectionObserver' in window) ? new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); } });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  // "Gallery lights" (.reveal-mask): images switch on as their tile enters the viewport (top at 95%).
  // Tiles that switch on together are staggered 0.08s apart.
  let lightsBatch = [];
  const lightUp = (el) => {
    if (el.classList.contains('is-in')) return;
    lightsBatch.push(el);
    if (lightsBatch.length > 1) return;
    queueMicrotask(() => {
      lightsBatch.forEach((m, i) => { m.style.setProperty('--lights-delay', `${Math.min(i, 6) * 0.08}s`); m.classList.add('is-in'); });
      lightsBatch = [];
    });
  };
  // Observe the tile (parent) so the trigger matches the ScrollTrigger path
  const maskIO = ('IntersectionObserver' in window) ? new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting) return;
      (en.target._masks || []).forEach(lightUp);
      maskIO.unobserve(en.target);
    });
  }, { rootMargin: '0px 0px -5% 0px', threshold: 0 }) : null;

  // 'loading' while GSAP is on its way, 'ready' once ScrollTrigger can take over masks
  let gsapState = 'none';
  const pendingMasks = [];
  const observeMask = (el) => {
    const parent = el.parentElement;
    (parent._masks = parent._masks || []).push(el);
    maskIO.observe(parent);
  };
  const revealMask = (el) => {
    if (el.classList.contains('is-in') || el._maskQueued) return;
    el._maskQueued = true;
    // Already in view (e.g. on page load): switch on now rather than waiting for GSAP
    if (el.parentElement.getBoundingClientRect().top < window.innerHeight * 0.95) { lightUp(el); return; }
    if (gsapState === 'ready') {
      window.ScrollTrigger.create({ trigger: el.parentElement, start: 'top 95%', once: true, onEnter: () => lightUp(el) });
    } else if (gsapState === 'loading') pendingMasks.push(el);
    else observeMask(el);
  };

  const initReveal = (root = document) => {
    if (document.documentElement.classList.contains('no-reveal')) return;
    $$('.reveal, .reveal-fade, .reveal-mask', root).forEach((el) => {
      if (!revealIO || reduceMotion) { el.classList.add('is-in'); return; }
      if (el.classList.contains('reveal-mask')) revealMask(el);
      else revealIO.observe(el);
    });
  };

  // Safety net: anything already above or inside the viewport once the page has loaded is shown
  const revealPassed = () => {
    $$('.reveal:not(.is-in), .reveal-fade:not(.is-in), .reveal-mask:not(.is-in)').forEach((el) => {
      const isMask = el.classList.contains('reveal-mask');
      const box = (isMask ? el.parentElement : el).getBoundingClientRect();
      if (box.top < window.innerHeight) { if (isMask) lightUp(el); else el.classList.add('is-in'); }
    });
  };
  if (document.readyState === 'complete') setTimeout(revealPassed, 0);
  else window.addEventListener('load', revealPassed, { once: true });

  /* ---------- Motion: GSAP + Lenis, loaded only when the page needs them ---------- */
  const motion = (window.theme && window.theme.motion) || {};
  const libs = (window.theme && window.theme.assets) || {};
  const motionOn = motion.enabled !== false && !reduceMotion && !(window.Shopify && window.Shopify.designMode);
  const desktopPointer = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches && navigator.maxTouchPoints === 0;
  let lenis = null;

  // Injected scripts with async=false run in insertion order without blocking parsing, like defer
  const loadScript = (src) => new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = false; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });

  const showSplit = () => {
    document.documentElement.classList.remove('motion-on');
  };

  const initSplit = (root = document) => {
    if (!window.SplitText) return;
    $$('[data-split]', root).forEach((el) => {
      if (el._split) return;
      const delay = parseFloat(el.dataset.splitDelay) || 0;
      el._split = window.SplitText.create(el, {
        type: 'lines', mask: 'lines', linesClass: 'split-line', autoSplit: true,
        onSplit: (self) => {
          el.classList.add('is-split');
          return window.gsap.from(self.lines, {
            yPercent: 105, duration: 1.2, ease: 'expo.out', stagger: 0.11, delay,
            scrollTrigger: { trigger: el, start: 'top 88%', once: true }
          });
        }
      });
    });
  };

  const initParallax = (root = document) => {
    $$('[data-parallax]', root).forEach((el) => {
      if (el._parallax) return;
      // Images carry 12% overscan, so keep travel under ~10% of their height
      const amount = Math.min((parseFloat(el.dataset.parallax) || 0.12) * 50, 9.5);
      el._parallax = window.gsap.fromTo(el, { yPercent: -amount }, {
        yPercent: amount, ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
      });
    });
  };

  // Large mosaic tiles and editorial images drift inside their frame on desktop (±3.3% of the 110% tall
  // image ≈ 7% travel). Drives --drift, which base.css feeds into `translate`, so it stacks with the
  // gallery-lights scale and the hover zoom
  const DRIFT_IMAGES = '.mosaic--layout-a:not(.mosaic--six) .mosaic__item:nth-child(5n+1) .media > img, .mosaic--six .mosaic__item:first-child .media > img, .editorial__media > img';
  const initFrameDrift = () => {
    window.gsap.matchMedia().add('(min-width: 990px)', () => {
      $$(DRIFT_IMAGES).forEach((img) => {
        window.gsap.fromTo(img, { '--drift': '-3.3%' }, {
          '--drift': '3.3%', ease: 'none',
          scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
        });
      });
    });
  };

  const initHeroZoom = (root = document) => {
    $$('.hero', root).forEach((hero) => {
      const media = $('.hero__slides', hero);
      if (!media || media._zoom) return;
      media._zoom = window.gsap.to(media, {
        scale: 1.12, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
      });
    });
  };

  const initLenis = () => {
    lenis = new window.Lenis({
      anchors: { offset: -(parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 76) },
      // Let drawers, modals and the search overlay scroll natively
      prevent: (node) => !!node.closest('.drawer, .modal, .menu-drawer, .search-overlay, [data-lenis-prevent]'),
      autoRaf: !window.gsap
    });
    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((time) => lenis.raf(time * 1000));
      window.gsap.ticker.lagSmoothing(0);
    }
    if (lockCount) lenis.stop();
  };

  const initMotion = () => {
    if (!motionOn) { showSplit(); return; }
    const want = {
      split: motion.reveal && !!$('[data-split]'),
      parallax: motion.parallax && !!$(`[data-parallax], .hero__slides, ${DRIFT_IMAGES}`),
      lenis: motion.smoothScroll && desktopPointer
    };
    const needGsap = want.split || want.parallax;
    const queue = [];
    if (needGsap) queue.push(libs.gsap, libs.scrollTrigger);
    if (want.split) queue.push(libs.splitText);
    if (want.lenis) queue.push(libs.lenis);
    if (!queue.length) { showSplit(); return; }
    // Masks queued from here on wait for ScrollTrigger (reveal masks only when reveals are on)
    if (needGsap && motion.reveal) gsapState = 'loading';

    Promise.all(queue.map(loadScript)).then(() => {
      if (needGsap) {
        window.gsap.registerPlugin(window.ScrollTrigger);
        if (want.split) window.gsap.registerPlugin(window.SplitText);
      }
      if (want.lenis) initLenis();
      if (want.split) {
        const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
        fontsReady.then(() => initSplit());
      } else showSplit();
      if (want.parallax) { initParallax(); initHeroZoom(); initFrameDrift(); }
      if (gsapState === 'loading') { gsapState = 'ready'; pendingMasks.splice(0).forEach((el) => { el._maskQueued = false; revealMask(el); }); }
      // Recalculate once images and fonts have settled the layout
      if (needGsap) {
        if (document.readyState === 'complete') window.ScrollTrigger.refresh();
        else window.addEventListener('load', () => window.ScrollTrigger.refresh(), { once: true });
      }
    }).catch(() => {
      showSplit();
      // GSAP never arrived: hand queued masks to the IntersectionObserver fallback
      gsapState = 'none';
      pendingMasks.splice(0).forEach(observeMask);
    });
  };

  /* ---------- Cursor ---------- */
  const cursor = $('.cursor-dot');
  if (cursor && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const label = cursor.querySelector('span');
    let x = 0, y = 0, cx = 0, cy = 0, raf;
    const loop = () => { cx += (x - cx) * 0.2; cy += (y - cy) * 0.2; cursor.style.transform = `translate3d(${cx}px, ${cy}px, 0)`; raf = requestAnimationFrame(loop); };
    document.addEventListener('mousemove', (e) => { x = e.clientX; y = e.clientY; if (!raf) loop(); }, { passive: true });
    document.addEventListener('mouseover', (e) => {
      const t = e.target.closest('[data-cursor]');
      if (t) { label.textContent = t.dataset.cursor; cursor.classList.add('is-active'); } else cursor.classList.remove('is-active');
    });
  }

  /* ---------- Cart ---------- */
  const Cart = {
    sections: () => ['cart-drawer'].filter((id) => document.getElementById('shopify-section-' + id)),
    async add(formData) {
      const sections = this.sections();
      if (sections.length) { formData.append('sections', sections.join(',')); formData.append('sections_url', location.pathname); }
      const res = await fetch(routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
      this.render(data.sections);
      await this.refreshCount();
      return data;
    },
    async change(line, quantity) {
      const sections = this.sections();
      const res = await fetch(routes.cartChange + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections, sections_url: location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.error);
      this.render(data.sections);
      this.setCount(data.item_count);
      if (document.body.classList.contains('template-cart')) location.reload();
      return data;
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const target = document.getElementById('shopify-section-' + id);
        if (!target) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById('shopify-section-' + id) || doc.body.firstElementChild;
        const inner = target.querySelector('[data-cart-inner]');
        const freshInner = fresh && fresh.querySelector('[data-cart-inner]');
        if (inner && freshInner) inner.replaceWith(freshInner); else if (fresh) target.innerHTML = fresh.innerHTML;
      });
    },
    async refreshCount() {
      try { const res = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } }); const c = await res.json(); this.setCount(c.item_count); } catch (_) {}
    },
    setCount(n) { $$('[data-cart-count]').forEach((el) => { el.textContent = n > 0 ? n : ''; el.dataset.count = n; }); }
  };
  window.BemoraCart = Cart;

  const setBtnLoading = (btn, on) => { if (!btn) return; btn.classList.toggle('is-loading', on); btn.setAttribute('aria-disabled', on ? 'true' : 'false'); };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    if (window.theme && window.theme.cartType === 'page') return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    setBtnLoading(btn, true);
    try {
      await Cart.add(new FormData(form));
      openPanel('cart-drawer');
    } catch (err) { toast(err.message); }
    finally { setBtnLoading(btn, false); }
  });

  document.addEventListener('click', async (e) => {
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      const fd = new FormData(); fd.append('id', quick.dataset.quickAdd); fd.append('quantity', '1');
      if (window.theme && window.theme.cartType === 'page') {
        setBtnLoading(quick, true);
        await fetch(routes.cartAdd + '.js', { method: 'POST', body: fd }); location.href = routes.cart; return;
      }
      setBtnLoading(quick, true);
      try { await Cart.add(fd); openPanel('cart-drawer'); } catch (err) { toast(err.message); } finally { setBtnLoading(quick, false); }
      return;
    }
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      const item = remove.closest('.cart-item'); if (item) item.classList.add('is-removing');
      try { await Cart.change(parseInt(remove.dataset.cartRemove, 10), 0); } catch (err) { toast(err.message); if (item) item.classList.remove('is-removing'); }
      return;
    }
    const step = e.target.closest('[data-qty-step]');
    if (step) {
      e.preventDefault();
      const wrap = step.closest('.qty'); const input = wrap.querySelector('input');
      const v = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyStep, 10));
      input.value = v;
      if (wrap.dataset.line) { try { await Cart.change(parseInt(wrap.dataset.line, 10), v); } catch (err) { toast(err.message); } }
    }
  });

  /* ---------- Predictive search ---------- */
  const search = $('#search-overlay');
  if (search) {
    const input = $('input[type="search"]', search);
    const results = $('[data-predictive-results]', search);
    let ctrl;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (!results) return;
      if (q.length < 2) { results.innerHTML = ''; return; }
      if (ctrl) ctrl.abort(); ctrl = new AbortController();
      try {
        const res = await fetch(`${routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=8`, { signal: ctrl.signal });
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-predictive-inner]');
        results.innerHTML = inner ? inner.outerHTML : '';
      } catch (_) {}
    }, 220);
    if (input && search.dataset.predictive === 'true') input.addEventListener('input', run);
  }

  /* ---------- Tabs ---------- */
  const initTabs = (root = document) => {
    $$('[data-tabs]', root).forEach((wrap) => {
      const tabs = $$('[role="tab"]', wrap);
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        tabs.forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
        $$('[role="tabpanel"]', wrap).forEach((p) => {
          const show = p.id === tab.getAttribute('aria-controls');
          p.hidden = !show;
          if (show) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); }
        });
      }));
    });
  };

  /* ---------- Rails ---------- */
  const initRails = (root = document) => {
    $$('[data-rail]', root).forEach((wrap) => {
      const rails = $$('.rail', wrap); if (!rails.length && wrap.classList.contains('rail')) rails.push(wrap);
      rails.forEach((rail) => { if (rail._init) return; rail._init = true;
      const scope = rail.parentElement;
      const prev = wrap.dataset.railButtons !== undefined ? $('.rail-btn--prev', wrap) : null, next = wrap.dataset.railButtons !== undefined ? $('.rail-btn--next', wrap) : null, bar = $('.rail-progress', scope) || (rail.nextElementSibling && rail.nextElementSibling.querySelector('.rail-progress'));
      const step = () => rail.clientWidth * 0.8;
      prev && prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      const prog = () => {
        if (!bar) return;
        const max = rail.scrollWidth - rail.clientWidth;
        const vis = rail.clientWidth / rail.scrollWidth;
        bar.style.setProperty('--p', Math.min(100, (vis + (max ? rail.scrollLeft / max : 0) * (1 - vis)) * 100) + '%');
      };
      rail.addEventListener('scroll', prog, { passive: true }); prog();
      // Drag to scroll (mouse only)
      let down = false, sx = 0, sl = 0, moved = false;
      rail.addEventListener('mousedown', (e) => { down = true; moved = false; sx = e.pageX; sl = rail.scrollLeft; });
      window.addEventListener('mouseup', () => { if (down) { down = false; setTimeout(() => rail.classList.remove('is-dragging'), 0); } });
      rail.addEventListener('mousemove', (e) => { if (!down) return; const dx = e.pageX - sx; if (Math.abs(dx) > 5) { moved = true; rail.classList.add('is-dragging'); } rail.scrollLeft = sl - dx; });
      rail.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
      });
    });
  };

  /* ---------- Hero slideshow ---------- */
  // Background videos: desktop only, never with reduced motion, Save-Data / 2g / 3g, or in the theme editor
  const heroVideoAllowed = () => {
    const c = navigator.connection;
    return !reduceMotion && !(window.Shopify && window.Shopify.designMode)
      && window.matchMedia('(min-width: 1024px)').matches
      && !(c && (c.saveData || /2g|3g/.test(c.effectiveType || '')));
  };

  const initHero = (root = document) => {
    $$('[data-hero-slides]', root).forEach((hero) => {
      const slides = $$('.hero__slide', hero); const dots = $$('.hero__dot', hero.closest('.hero'));
      const time = parseInt(hero.dataset.heroSlides, 10) || 6000; let i = 0, t;

      // Only the active and next slide's videos get a src; hidden slides are paused
      const useVideo = !!$('[data-hero-video]', hero) && heroVideoAllowed();
      let started = false, inView = true;
      const videoOf = (s) => $('[data-hero-video]', s);
      const play = (v) => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
      const loadVideo = (s) => {
        const v = videoOf(s);
        if (!v || v.src) return;
        v.addEventListener('canplaythrough', () => {
          v.classList.add('is-playing');
          if (s.classList.contains('is-active') && inView) play(v);
        }, { once: true });
        // Poster = the still the browser already picked from its srcset, so no second download
        const still = $('img', s); if (still && still.currentSrc) v.poster = still.currentSrc;
        v.preload = 'auto'; v.src = v.dataset.src; v.load();
      };
      const syncVideos = () => {
        if (!useVideo || !started) return;
        loadVideo(slides[i]);
        if (slides.length > 1) loadVideo(slides[(i + 1) % slides.length]);
        slides.forEach((s, n) => {
          const v = videoOf(s); if (!v || !v.src) return;
          if (n === i && inView && v.classList.contains('is-playing')) play(v); else v.pause();
        });
      };
      if (useVideo) {
        // Start after the page has loaded so the first still image wins the bandwidth
        const start = () => { started = true; syncVideos(); };
        if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true });
        new IntersectionObserver(([en]) => { inView = en.isIntersecting; syncVideos(); }).observe(hero);
      }

      // Zoom (.is-zooming) restarts every time a slide becomes active; the outgoing slide keeps zooming
      // through its cross-dissolve and is only reset once it is fully faded out
      const fadeMs = () => (parseFloat(getComputedStyle(slides[0]).transitionDuration) || 2) * 1000;
      const startZoom = (s) => { clearTimeout(s._unzoom); s.classList.remove('is-zooming'); void s.offsetWidth; s.classList.add('is-zooming'); };
      const endZoom = (s) => {
        clearTimeout(s._unzoom);
        s._unzoom = setTimeout(() => { if (!s.classList.contains('is-active')) s.classList.remove('is-zooming'); }, fadeMs() + 100);
      };
      startZoom(slides[0]);

      if (slides.length < 2) return;
      const go = (n) => {
        const prev = slides[i];
        prev.classList.remove('is-active'); endZoom(prev); dots[i] && dots[i].classList.remove('is-active');
        i = (n + slides.length) % slides.length;
        slides[i].classList.add('is-active'); startZoom(slides[i]); if (dots[i]) { dots[i].classList.add('is-active'); }
        syncVideos();
        clearTimeout(t); t = setTimeout(() => go(i + 1), time);
      };
      dots.forEach((d, n) => d.addEventListener('click', () => go(n)));
      // The first slide's clock starts once it is actually on screen (after its decode + fade-in in hero.liquid)
      const begin = () => { if (!t) t = setTimeout(() => go(1), time); };
      const first = $('.hero__media', slides[0]);
      if (first && first.classList.contains('is-pending')) first.addEventListener('hero:shown', begin, { once: true }); else begin();
    });
  };

  /* ---------- Testimonials ---------- */
  const initQuotes = (root = document) => {
    $$('[data-quotes]', root).forEach((wrap) => {
      const items = $$('.quote', wrap); const dots = $$('.quotes__dot', wrap.parentElement);
      if (items.length < 2) return;
      let i = 0, t; const time = 7000;
      const go = (n) => {
        items[i].classList.remove('is-active'); dots[i] && dots[i].setAttribute('aria-current', 'false');
        i = (n + items.length) % items.length;
        items[i].classList.add('is-active'); dots[i] && dots[i].setAttribute('aria-current', 'true');
        clearTimeout(t); t = setTimeout(() => go(i + 1), time);
      };
      dots.forEach((d, n) => d.addEventListener('click', () => go(n)));
      t = setTimeout(() => go(1), time);
    });
  };

  /* ---------- Wall / room preview ---------- */
  const initRooms = (root = document) => {
    $$('[data-room]', root).forEach((wrap) => {
      if (wrap._init) return; wrap._init = true;
      const stage = $('.room-stage', wrap); if (!stage) return;
      const art = $('.room-art', stage); const artImg = $('.room-art__img img', stage);
      const setPressed = (group, btn) => $$(`[data-room-${group}]`, wrap).forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
      // Swap the artwork on the wall when a thumbnail is clicked
      wrap._setArt = (b) => {
        if (!artImg) return;
        setPressed('art', b);
        art.classList.add('is-swapping');
        // Room art is the watermark-free _PREVIEW file; if it is missing, fall back to the _WM image
        let src = b.dataset.roomArt, finished = false;
        const fallback = b.dataset.roomArtFallback;
        const next = new Image();
        const done = () => {
          if (finished) return; finished = true;
          artImg.removeAttribute('srcset');
          artImg.src = src; artImg.alt = b.dataset.roomTitle || '';
          stage.dataset.orient = b.dataset.roomOrient || 'portrait';
          setTimeout(() => art.classList.remove('is-swapping'), 60);
        };
        const t0 = performance.now();
        next.onload = () => setTimeout(done, Math.max(150, 250 - (performance.now() - t0)));
        next.onerror = () => { if (fallback && src !== fallback) { src = fallback; next.src = src; } else done(); };
        next.src = src;
        const t = $('[data-room-current-title]', wrap), p = $('[data-room-current-price]', wrap), l = $('[data-room-current-link]', wrap);
        if (t) t.textContent = b.dataset.roomTitle || '';
        if (p) p.innerHTML = b.dataset.roomPrice || '';
        if (l) l.href = b.dataset.roomUrl || '#';
      };

      wrap.addEventListener('click', (e) => {
        const b = e.target.closest('button'); if (!b || !wrap.contains(b)) return;
        if (b.dataset.roomFrame !== undefined) { stage.dataset.frame = b.dataset.roomFrame; setPressed('frame', b); }
        if (b.dataset.roomSize !== undefined) { stage.dataset.size = b.dataset.roomSize; setPressed('size', b); }
        if (b.dataset.roomMat !== undefined) { const on = stage.dataset.mat !== 'true'; stage.dataset.mat = on; b.setAttribute('aria-pressed', on); }
        if (b.dataset.roomTone !== undefined) {
          stage.dataset.tone = b.dataset.roomTone; setPressed('tone', b);
          $$('.room-stage__bg', stage).forEach((bg) => {
            const on = bg.dataset.bg === b.dataset.roomTone;
            bg.style.opacity = on ? '1' : '0';
            if (on && bg.dataset.top) stage.style.setProperty('--room-art-top', bg.dataset.top + '%');
          });
          const css = $('.room-stage__css', stage); if (css) css.style.opacity = b.dataset.roomPhoto ? '0' : '1';
        }
        if (b.dataset.roomArt !== undefined) wrap._setArt(b);
      });
    });
  };

  /* ---------- Product page ---------- */
  const initProduct = (root = document) => {
    $$('[data-product]', root).forEach((wrap) => {
      if (wrap._init) return; wrap._init = true;
      const jsonEl = $('[data-product-json]', wrap);
      const product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      const form = $('form[data-ajax-cart]', wrap) || $('form[action*="/cart/add"]', wrap);
      const idInput = form && $('input[name="id"]', form);
      const atc = form && $('[type="submit"]', form);
      const priceEl = $('[data-price]', wrap);

      // Gallery
      const slides = $('.gallery-slides', wrap); const thumbs = $$('.gallery-thumb', wrap); const counter = $('.gallery-counter', wrap);
      if (slides) {
        const items = $$('.gallery-slide', slides);
        const setActive = (idx) => { thumbs.forEach((t, n) => t.setAttribute('aria-current', n === idx ? 'true' : 'false')); if (counter) counter.textContent = `${idx + 1} / ${items.length}`; };
        thumbs.forEach((t, n) => t.addEventListener('click', () => { slides.scrollTo({ left: items[n].offsetLeft, behavior: 'smooth' }); setActive(n); }));
        slides.addEventListener('scroll', debounce(() => { const idx = Math.round(slides.scrollLeft / slides.clientWidth); setActive(idx); }, 60), { passive: true });
        // hover zoom (desktop)
        if (window.matchMedia('(hover: hover)').matches) {
          $$('.gallery-slide .media[data-zoom]', wrap).forEach((m) => {
            m.addEventListener('click', () => m.classList.toggle('is-zoomed'));
            m.addEventListener('mousemove', (e) => { const r = m.getBoundingClientRect(); m.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100) + '%'); m.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100) + '%'); });
            m.addEventListener('mouseleave', () => m.classList.remove('is-zoomed'));
          });
        }
        wrap._goToMedia = (mediaId) => { const n = items.findIndex((it) => it.dataset.mediaId === String(mediaId)); if (n > -1) { slides.scrollTo({ left: items[n].offsetLeft, behavior: 'smooth' }); setActive(n); } };
      }

      // Variants
      if (product && product.variants.length > 1) {
        const fieldsets = $$('.variant-group', wrap);
        const onChange = () => {
          const selected = fieldsets.map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });
          fieldsets.forEach((fs, i) => { const v = $('[data-selected-value]', fs); if (v) v.textContent = selected[i] || ''; });
          const variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
          // availability marks
          fieldsets.forEach((fs, i) => {
            $$('input', fs).forEach((inp) => {
              const test = selected.slice(); test[i] = inp.value;
              const exists = product.variants.find((v) => v.options.every((o, k) => o === test[k]) && v.available);
              inp.classList.toggle('is-unavailable', !exists);
            });
          });
          if (!variant) { if (atc) { atc.setAttribute('aria-disabled', 'true'); atc.querySelector('span').textContent = strings.soldOut || 'Unavailable'; } return; }
          if (idInput) idInput.value = variant.id;
          if (atc) { atc.setAttribute('aria-disabled', variant.available ? 'false' : 'true'); atc.disabled = !variant.available; const s = atc.querySelector('span'); if (s) s.textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out'); }
          if (priceEl && wrap.dataset.moneyFormat) {
            const fmt = (c) => wrap.dataset.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => { const n = (c / 100); return k.includes('no_decimals') ? Math.round(n).toLocaleString() : k.includes('comma') ? n.toFixed(2).replace('.', ',') : n.toFixed(2); });
            priceEl.innerHTML = variant.compare_at_price > variant.price ? `<s data-eur="${variant.compare_at_price}">${fmt(variant.compare_at_price)}</s><span data-eur="${variant.price}">${fmt(variant.price)}</span>` : `<span data-eur="${variant.price}">${fmt(variant.price)}</span>`;
            // Sticky bar and wall-preview button: EUR text first, then data-eur, so currency.js re-converts them
            $$('[data-variant-price]', wrap.closest('.shopify-section') || document).forEach((el) => { el.textContent = fmt(variant.price); el.dataset.eur = variant.price; });
          }
          if (variant.featured_media && wrap._goToMedia) wrap._goToMedia(variant.featured_media.id);
          const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
        };
        fieldsets.forEach((fs) => fs.addEventListener('change', onChange));
      }

      // Sticky ATC
      const sticky = $('.sticky-atc', wrap) || $('.sticky-atc');
      if (sticky && atc && 'IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => { sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0); }).observe(atc);
        const sBtn = $('[data-sticky-submit]', sticky);
        sBtn && sBtn.addEventListener('click', (e) => { e.preventDefault(); if (form.requestSubmit) form.requestSubmit(atc); else atc.click(); });
      }
    });
  };

  /* ---------- Collection filtering ---------- */
  const initCollection = () => {
    const wrap = $('[data-collection]'); if (!wrap) return;
    const sectionId = wrap.dataset.sectionId;
    const fetchAndRender = async (url, append = false) => {
      const grid = $('[data-collection-grid]', wrap); grid && grid.classList.add('is-loading');
      const u = new URL(url, location.origin); u.searchParams.set('section_id', sectionId);
      try {
        const res = await fetch(u.toString()); const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-collection]');
        if (!fresh) { location.href = url; return; }
        if (append) {
          const freshGrid = $('[data-collection-grid]', fresh);
          $$('.card', freshGrid).forEach((c, i) => { c.style.setProperty('--i', i); c.classList.add('reveal'); grid.appendChild(c); });
          const lm = $('[data-load-more]', wrap), flm = $('[data-load-more]', fresh);
          if (lm) { if (flm) lm.replaceWith(flm); else lm.remove(); }
          initReveal(grid);
        } else {
          ['[data-collection-grid]', '[data-load-more]', '.pagination', '[data-active-filters]', '[data-product-count]', '[data-filter-form]'].forEach((sel) => {
            const a = $(sel, wrap), b = $(sel, fresh);
            if (a && b) a.replaceWith(b); else if (a && !b) a.remove();
            else if (!a && b && sel === '[data-load-more]') { const g = $('[data-collection-grid]', wrap); g && g.after(b); }
          });
          initReveal(wrap);
          history.replaceState({}, '', url);
        }
      } catch (_) { location.href = url; }
      finally { const g = $('[data-collection-grid]', wrap); g && g.classList.remove('is-loading'); }
    };
    const formToUrl = (form) => {
      const params = new URLSearchParams(new FormData(form));
      const sort = $('[data-sort]', wrap); if (sort && sort.value) params.set('sort_by', sort.value);
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      return `${location.pathname}?${params.toString()}`;
    };
    wrap.addEventListener('change', debounce((e) => {
      const form = e.target.closest('[data-filter-form]');
      if (form) fetchAndRender(formToUrl(form));
      else if (e.target.matches('[data-sort]')) { const f = $('[data-filter-form]', wrap); if (f) fetchAndRender(formToUrl(f)); else { const u = new URL(location.href); u.searchParams.set('sort_by', e.target.value); fetchAndRender(u.pathname + u.search); } }
    }, 350));
    wrap.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-filter-remove]'); if (rm) { e.preventDefault(); fetchAndRender(rm.getAttribute('href')); return; }
      const lm = e.target.closest('[data-load-more] a'); if (lm) { e.preventDefault(); lm.classList.add('is-loading'); fetchAndRender(lm.getAttribute('href'), true); return; }
      const g = e.target.closest('[data-grid-cols]'); if (g) {
        const grid = $('[data-collection-grid]', wrap); grid.style.setProperty('--cols', g.dataset.gridCols);
        $$('[data-grid-cols]', wrap).forEach((b) => b.setAttribute('aria-pressed', b === g ? 'true' : 'false'));
        try { localStorage.setItem('bemora-grid', g.dataset.gridCols); } catch (_) {}
      }
    });
    try { const saved = localStorage.getItem('bemora-grid'); if (saved) { const b = $(`[data-grid-cols="${saved}"]`, wrap); b && b.click(); } } catch (_) {}
  };

  /* ---------- Init ---------- */
  const initAll = (root = document) => {
    initReveal(root); initTabs(root); initRails(root); initHero(root); initQuotes(root); initRooms(root); initProduct(root);
  };
  // initMotion first, so initReveal knows whether masks should wait for ScrollTrigger
  const start = () => { initMotion(); initAll(); initCollection(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

  // Theme editor support (motion never runs here, so headings are never split)
  document.addEventListener('shopify:section:load', (e) => { initAll(e.target); });
  document.addEventListener('shopify:section:select', (e) => { $$('.reveal, .reveal-fade, .reveal-mask', e.target).forEach((el) => el.classList.add('is-in')); });
  if (window.Shopify && window.Shopify.designMode) document.documentElement.classList.add('is-design-mode');
})();
